/**
 * Componente responsável por exibir e gerenciar a tabela de processos.
 * Implementa funcionalidades de CRUD para processos, incluindo filtros e paginação.
 * Oferece suporte para operações offline, como criação e edição de processos,
 * e sincroniza automaticamente quando a conexão é restaurada.
 */

import {
  Component,
  Input,
  OnInit,
  OnChanges,
  AfterViewInit,
  ViewChild,
  SimpleChanges,
  ViewEncapsulation,
  ChangeDetectionStrategy,
  Renderer2,
  Inject,
} from '@angular/core';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { SelectionModel } from '@angular/cdk/collections';
import {
  catchError,
  concatMap,
  debounceTime,
  distinctUntilChanged,
  mergeMap,
  startWith,
  takeUntil,
  tap,
} from 'rxjs/operators';
import { Process, ProcessStatus } from 'src/app/core/models/process.model';
import { ProcessService } from 'src/app/features/process/services/process.service';
import { ConfirmDialogComponent } from 'src/app/features/process/components/dialogs/actions/confirm-dialog/confirm-dialog.component';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { CommonModule } from '@angular/common';
import { MatFormField, MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatSelectModule } from '@angular/material/select';
import { ProcessTypeDialogComponent } from 'src/app/features/process/components/dialogs/process-creation/process-type-dialog/process-type-dialog.component';
import { ProcessDialogComponent } from 'src/app/features/process/components/dialogs/process-creation/process-dialog/process-dialog.component';
import { MatToolbarModule } from '@angular/material/toolbar';
import { ProcessPreviewDialogComponent } from 'src/app/features/process/components/dialogs/actions/process-preview-dialog/process-preview-dialog';
import { ProcessEditDialogComponent } from 'src/app/features/process/components/dialogs/actions/process-edit-dialog/process-edit-dialog.component';
import { MatRadioModule } from '@angular/material/radio';
import { StorageService } from 'src/app/core/services/StorageService/storage-service.service';
import { combineLatest, from, Observable, pipe, Subject } from 'rxjs';
import { Injectable } from '@angular/core';
import { ErrorHandlingService } from 'src/app/core/services/ErrorHandlingService/errorHandlingService.service';
import { ProcessFilterDialogComponent } from 'src/app/features/process/components/shared/process-filter/process-filter-dialog.component';
import { ProcessSyncComponent } from 'src/app/features/process/components/shared/process-sync/process-sync.component';
import { SyncService } from 'src/app/core/services/SyncService/syncService.service';
import { JSONSchema } from '@ngx-pwa/local-storage';
import { BehaviorSubject } from 'rxjs';

@Component({
  selector: 'app-process-table',
  standalone: true,
  templateUrl: './process-table.component.html',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    MatCheckboxModule,
    MatIconModule,
    MatTableModule,
    MatPaginatorModule,
    MatSortModule,
    ReactiveFormsModule,
    MatSnackBarModule,
    MatDialogModule,
    ConfirmDialogComponent,
    MatFormField,
    MatFormFieldModule,
    MatInputModule,
    MatExpansionModule,
    MatSelectModule,
    ProcessTypeDialogComponent,
    MatToolbarModule,
    ProcessPreviewDialogComponent,
    MatRadioModule,
    ProcessFilterDialogComponent,
    ProcessSyncComponent,
  ],
})
@Injectable({
  providedIn: 'root',
})
export class ProcessTableComponent implements OnInit, OnChanges, AfterViewInit {
  @Input() processes: Process[] = [];
  displayedColumns: string[] = ['actions', 'numeroProcesso', 'empresa', 'descricao', 'tema', 'situacao', 'dataInclusao'];
  dataSource = new MatTableDataSource<Process>(this.processes);
  private processesSubject = new BehaviorSubject<Process[]>([]);
  selection = new SelectionModel<Process>(true, []);
  private readonly schema: JSONSchema = {
    type: 'object',
    properties: {
      id: { type: 'number' },
      descricao: { type: 'string' },
    },
    required: ['id', 'descricao'],
  };

  filterControl = new FormControl('');
  empresaControl = new FormControl('');
  situacaoRequerimentoControl = new FormControl('');
  modalidadeControl = new FormControl('');
  faseControl = new FormControl('');
  cpfCnpjControl = new FormControl('');
  numeroProcessoControl = new FormControl('');
  cpfCnpjRequerenteControl = new FormControl('');

  @ViewChild(ProcessFilterDialogComponent)
  processFilterDialogComponent!: ProcessFilterDialogComponent;
  @ViewChild(ProcessSyncComponent) processSyncComponent!: ProcessSyncComponent;

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;
  indexedDbService: any;
  private destroy$ = new Subject<void>();
  processes$ = this.processesSubject.asObservable();
  isLoading = true;
  realDataLoaded = false;

  constructor(
    private processService: ProcessService,
    private storageService: StorageService,
    private snackBar: MatSnackBar,
    private dialog: MatDialog,
    @Inject(ErrorHandlingService)
    private errorHandlingService: ErrorHandlingService,
    private renderer: Renderer2,
    private syncService: SyncService
  ) {
    this.dataSource = new MatTableDataSource<Process>([]);
  }

  ngOnInit(): void {
    this.setupDataSource();

    // Carrega dados reais depois de configurar o processo fixo
    this.loadRealProcesses();

    // Configura o dataSource com os processos
    this.dataSource.data = this.processes;
    this.dataSource._updateChangeSubscription(); // Garante a atualização da tabela
    console.log('DataSource após atualização:', this.dataSource.data);

    // Configura o paginator e sort
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;

    // Aplica filtros ao dataSource
    this.applyFilterOnChange();

    // Observa mudanças no processesSubject para refletir em processes$
    this.processes$ = this.processesSubject.asObservable();

    // Sincroniza processos offline quando a conexão é restaurada
    window.addEventListener('online', () => this.syncOfflineProcesses());
  }

  private setupDataSource(): void {
    this.dataSource.filterPredicate = (data: Process, filter: string) => {
      const searchStr = JSON.stringify(data).toLowerCase();
      return searchStr.indexOf(filter.toLowerCase()) !== -1;
    };
  }

  private updateDataSource(processes: Process[]): void {
    this.dataSource.data = processes;
    this.processesSubject.next(processes);
    this.dataSource._updateChangeSubscription();
    console.log('DataSource updated with:', processes);
  }

  private setupPaginatorAndSort(): void {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;

    if (this.sort) {
      this.sort.sortChange.subscribe(() => {
        if (this.paginator) {
          this.paginator.pageIndex = 0;
        }
      });
    }
  }

  private loadRealProcesses(): void {
    this.processService.getProcessesOffline(this.schema).subscribe({
      next: (processes: Process[]) => {
        if (processes.length > 0) {
          this.dataSource.data = processes; // Atualiza o dataSource com dados reais
          this.realDataLoaded = true; // Marca que os dados reais foram carregados
        }
      },
      error: (err: any) => this.handleError(err, 'Erro ao carregar processos!'),
    });
  }

  // Novo método para sincronização de processos offline
  private syncOfflineProcesses(): void {
    if (this.processSyncComponent) {
      this.processSyncComponent.syncOfflineProcesses();
    }
  }

  isDataAvailable(): boolean {
    const storedProcesses =
      this.storageService.getObject<Process[]>('processes');
    return !!storedProcesses?.length && storedProcesses.length > 0;
  }

  private fetchProcessesFromBackend(): void {
    this.processService.getProcessesOffline(this.schema).subscribe({
      next: (processes: Process[]) => {
        console.log('Processos carregados do backend:', processes);
        this.dataSource.data = processes;
        this.storageService.setObject('processes', processes);
        this.dataSource._updateChangeSubscription();
      },
      error: (err: any) => this.handleError(err, 'Erro ao carregar processos!'),
    });
  }

  applyFilter(filterValue: string): void {
    this.dataSource.filter = filterValue.trim().toLowerCase();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['processes'] && changes['processes'].currentValue) {
      this.dataSource.data = changes['processes'].currentValue;
      this.dataSource._updateChangeSubscription();

      console.log("Processos recebidos no ProcessTableComponent:", this.processes);
    }
  }

  // Aplicar os filtros
  applyFilters(): void {
    this.dataSource.filter = 'apply'; // Aciona o filtro
  }

  clearFilters(): void {
    this.filterControl.reset();
    // Limpar também os controles específicos de filtro
    const controls = [
      this.numeroProcessoControl,
      this.empresaControl,
      this.situacaoRequerimentoControl,
      this.modalidadeControl,
      this.faseControl,
      this.cpfCnpjControl,
    ];
    controls.forEach((control) => control.reset());

    // Atualizar a fonte de dados
    this.applyFilter('');
  }

  ngAfterViewInit(): void {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
    this.setupPaginatorAndSort();
  }

  applyFilterOnChange(): void {
    combineLatest([
      this.numeroProcessoControl.valueChanges.pipe(startWith('')),
      this.empresaControl.valueChanges.pipe(startWith('')),
      this.situacaoRequerimentoControl.valueChanges.pipe(startWith('')),
      this.modalidadeControl.valueChanges.pipe(startWith('')),
      this.faseControl.valueChanges.pipe(startWith('')),
      this.cpfCnpjControl.valueChanges.pipe(startWith('')),
    ])
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(([numero, empresa, situacao, modalidade, fase, cpfCnpj]) => {
        this.dataSource.filter =
          `${numero} ${empresa} ${situacao} ${modalidade} ${fase} ${cpfCnpj}`
            .trim()
            .toLowerCase();
      });
  }

  checkMatch(field: string, value: string): boolean {
    return value ? field.toLowerCase().includes(value.toLowerCase()) : true;
  }

  checkTipoRequerente(tipo: string, data: Process): boolean {
    if (tipo === 'fisica') {
      return data.empresa.cpfCnpj.length === 11;
    }
    if (tipo === 'juridica') {
      return data.empresa.cpfCnpj.length === 14;
    }
    if (tipo === 'tecnico') {
      return (
        data.empresa.cpfCnpj.length === 11 || data.empresa.cpfCnpj.length === 14
      );
    }
    return true;
  }

  toggle(element: Process): void {
    this.selection.toggle(element);
  }

  isAllSelected(): boolean {
    const numSelected = this.selection.selected.length;
    const numRows = this.dataSource.data.length;
    return numSelected === numRows;
  }

  masterToggle(): void {
    this.isAllSelected()
      ? this.selection.clear()
      : this.dataSource.data.forEach((row) => this.selection.select(row));
  }

  openDetailsDialog(process: Process): void {
    this.dialog.open(ProcessPreviewDialogComponent, {
      width: '400px',
      data: process,
    });
  }

  getStatusClass(situacao: string): string {
    switch (situacao) {
      case 'Aguardando':
        return 'status-aguardando';
      case 'Aguardando pagamento':
        return 'status-aguardando-pagamento';
      case 'Atrasado':
        return 'status-atrasado';
      case 'Cancelado':
        return 'status-cancelado';
      case 'Finalizado':
        return 'status-finalizado';
      case 'Em análise':
        return 'status-em-analise';
      case 'Rascunho':
        return 'status-rascunho';
      case 'Pendente':
        return 'status-pendente';
      default:
        return '';
    }
  }

  editProcess(process: Process): void {
    const dialogRef = this.dialog.open(ProcessEditDialogComponent, {
      width: '400px',
      data: process,
      maxHeight: '90vh',
    });

    // Adicionando 'takeUntil(this.destroy$)' corretamente
    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result: Process | null) => {
        if (result) {
          let storedProcesses =
            this.storageService.getObject<Process[]>('processes') || [];
          const index = storedProcesses.findIndex(
            (p: Process) => p.id === process.id
          );

          if (index !== -1) {
            storedProcesses[index] = result;
            this.storageService.setObject('processes', storedProcesses);

            // Atualiza a fonte de dados com o processo editado
            this.dataSource.data = storedProcesses;
            this.snackBar.open('Processo editado com sucesso!', 'Fechar', {
              duration: 3000,
            });
          }
        }
      });
  }

  openConfirmDeleteDialog(process: Process): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '300px',
      data: { message: `Deseja realmente excluir o processo ${process.id}?` },
      maxHeight: '90vh',
      panelClass: 'custom-dialog-container',
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.deleteProcess(process);
      }
    });
  }

  deleteProcess(process: Process): void {
    const schema = this.schema;
    this.processService.deleteProcess(process.id, schema).subscribe({
      next: () => {
        let storedProcesses =
          this.storageService.getObject<Process[]>('processes') || [];
        storedProcesses = storedProcesses.filter(
          (p: Process) => p.id !== process.id
        );
        this.storageService.setObject('processes', storedProcesses);

        this.dataSource.data = storedProcesses;
        this.dataSource._updateChangeSubscription();
        this.snackBar.open('Processo excluído com sucesso!', 'Fechar', {
          duration: 1000,
        });
      },
      error: (err: any) => this.handleError(err, 'Erro ao excluir processo!'),
    });
  }

  private applyAllFilters(): void {
    const filterValues = [
      this.numeroProcessoControl.value,
      this.empresaControl.value,
      this.situacaoRequerimentoControl.value,
    ];

    this.dataSource.filter = filterValues.join(' ').trim().toLowerCase();
  }

  private collectFilterValues(): string {
    const filterValues = [
      this.numeroProcessoControl.value,
      this.empresaControl.value,
      this.situacaoRequerimentoControl.value,
      this.cpfCnpjRequerenteControl.value,
    ];

    return filterValues
      .filter((value) => !!value)
      .join(' ')
      .trim()
      .toLowerCase();
  }

  loadProcesses(): void {
    let storedProcesses =
      this.storageService.getObject<Process[]>('processes') || [];
    console.log('Processos armazenados localmente:', storedProcesses);

    if (storedProcesses.length > 0) {
      this.dataSource.data = storedProcesses;
      this.dataSource._updateChangeSubscription();
    } else {
      this.processService
        .getProcessesOffline(this.schema)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (processes: Process[]) => {
            this.processes = processes;
            this.dataSource.data = processes;
          },
          error: (err) => console.error('Erro ao carregar processos', err),
        });
    }
  }

  trackByFn(index: number, item: Process): number {
    return item?.id ?? index;
  }

  private handleError(error: any, message: string): void {
    this.errorHandlingService.handleError(error, message);
  }
}
